import { toast } from "react-toastify";
import { handleError } from "../../../utils/helpers";
import { BaseApiResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import { authSliceActions } from "./auth.slice";
import {
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  GoogleAuthenticationPayload,
  GoogleAuthenticationResponse,
  LoginPayload,
  LoginResponse,
  ResendOtpPayload,
  ResendOtpResponse,
  ResetPasswordPayload,
  SignUpPayload,
  SignUpResponse,
  UpdateProfilePayload,
  VerifyEmailPayload,
  VerifyEmailResponse,
} from "./auth.types";

export const authApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    signUp: builder.mutation<SignUpResponse, SignUpPayload>({
      query: (payload) => {
        return {
          url: "auth/signup",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    login: builder.mutation<LoginResponse, LoginPayload>({
      query: (payload) => {
        return {
          url: "auth/login",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    verifyEmail: builder.mutation<VerifyEmailResponse, VerifyEmailPayload>({
      query: (payload) => {
        return {
          url: "auth/verify-otp",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    resendOtp: builder.mutation<ResendOtpResponse, ResendOtpPayload>({
      query: (payload) => {
        return {
          url: "auth/resend-otp",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Code Resent");
        } catch (err) {
          handleError(err);
        }
      },
    }),
    forgotPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordPayload
    >({
      query: (payload) => {
        return {
          url: "auth/forgot-password",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    resetPassword: builder.mutation<BaseApiResponse, ResetPasswordPayload>({
      query: (payload) => {
        return {
          url: "auth/reset-password",
          method: "PATCH",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    googleAuthentication: builder.mutation<
      GoogleAuthenticationResponse,
      GoogleAuthenticationPayload
    >({
      query: (payload) => {
        return {
          url: "auth/google-auth",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    updateProfile: builder.mutation<LoginResponse, UpdateProfilePayload>({
      query: (payload) => {
        return {
          url: "auth/update-profile",
          method: "PATCH",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          toast.success("Details Saved");

          dispatch(
            authSliceActions.updateProfile({
              profile: {
                ...data.data,
              },
            }),
          );
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useSignUpMutation,
  useVerifyEmailMutation,
  useResendOtpMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGoogleAuthenticationMutation,
  useUpdateProfileMutation,
} = authApi;
